
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Users, Wheat, Plus, TrendingUp, Calendar, MapPin, BarChart3, ShoppingCart } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCoordinatorFarms, useCoordinatorStats } from '@/hooks/use-coordinator';
import { useGetHarvests } from '@/hooks/use-harvest';
import CoordinatorProcurementSection from '@/components/coordinators/CoordinatorProcurementSection';
import CoordinatorAnalytics from '@/components/coordinators/CoordinatorAnalytics';

const CoordinatorDashboard: React.FC = () => {
  const { data: farms = [] } = useCoordinatorFarms();
  const { data: stats } = useCoordinatorStats();
  const { data: harvests = [] } = useGetHarvests();

  const totalFarms = stats?.totalFarms || 0;
  const upcomingHarvests = harvests.filter(harvest => 
    harvest.status === 'planned' && 
    harvest.expected_date && 
    new Date(harvest.expected_date) > new Date()
  ).length;
  const completedHarvests = harvests.filter(harvest => harvest.status === 'completed').length;

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Coordinator Dashboard</h1>
          <p className="text-muted-foreground">Manage farmers, coordinate harvests, and track procurement performance</p>
        </div>
        <div className="flex gap-2">
          <Button asChild>
            <Link to="/coordinators/farmers">
              <Users className="h-4 w-4 mr-2" />
              Manage Farmers
            </Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/coordinators/harvests">
              <Wheat className="h-4 w-4 mr-2" />
              Manage Harvests
            </Link>
          </Button>
        </div>
      </div>

      {/* Overview Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Farmers</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalFarms}</div>
            <p className="text-xs text-muted-foreground">
              Farmers registered by you
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Phone Access</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.farmsWithPhoneAccess || 0}</div>
            <p className="text-xs text-muted-foreground">
              Out of {totalFarms} farmers
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Upcoming Harvests</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{upcomingHarvests}</div>
            <p className="text-xs text-muted-foreground">
              Next 30 days
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completed Harvests</CardTitle>
            <Wheat className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{completedHarvests}</div>
            <p className="text-xs text-muted-foreground">
              This season
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="procurement">Procurement</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="actions">Quick Actions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Recent Farmer Activity</CardTitle>
                <CardDescription>Latest farmers you've registered</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {farms.slice(0, 5).map((farm) => (
                    <div key={farm.id} className="flex items-center space-x-4">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{farm.farmer_name}</p>
                        <p className="text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3 inline mr-1" />
                          {farm.region} • {farm.product_name}
                        </p>
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {new Date(farm.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  ))}
                  {farms.length === 0 && (
                    <p className="text-sm text-muted-foreground">No farmers registered yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Farmer Engagement Stats</CardTitle>
                <CardDescription>Connection and contract overview</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Farmers with Phone Access</span>
                    <span className="font-medium">{stats?.farmsWithPhoneAccess || 0} / {totalFarms}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Farmers with Contracts</span>
                    <span className="font-medium">{stats?.farmsWithContracts || 0} / {totalFarms}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Recent Registrations</span>
                    <span className="font-medium">{stats?.recentRegistrations || 0} this week</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="procurement">
          <CoordinatorProcurementSection />
        </TabsContent>

        <TabsContent value="analytics">
          <CoordinatorAnalytics />
        </TabsContent>

        <TabsContent value="actions" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Farmer Management</CardTitle>
                <CardDescription>Register and manage farmers</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button asChild className="w-full justify-start">
                  <Link to="/farm-form">
                    <Plus className="h-4 w-4 mr-2" />
                    Register New Farmer
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start">
                  <Link to="/coordinators/farmers">
                    <Users className="h-4 w-4 mr-2" />
                    View All Farmers
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Harvest Management</CardTitle>
                <CardDescription>Create and track harvests</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button asChild className="w-full justify-start">
                  <Link to="/harvest-form">
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Harvest
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start">
                  <Link to="/harvest-calendar">
                    <Calendar className="h-4 w-4 mr-2" />
                    View Harvest Calendar
                  </Link>
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Procurement</CardTitle>
                <CardDescription>Manage purchase orders</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button asChild className="w-full justify-start">
                  <Link to="/procurement-form">
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Create Procurement
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start">
                  <Link to="/procurements">
                    <BarChart3 className="h-4 w-4 mr-2" />
                    View All Procurements
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CoordinatorDashboard;
